import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, KeyRound } from "lucide-react";
import { authAxios, formatApiErrorDetail } from "@/context/AuthContext";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authAxios.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="max-w-md mx-auto px-4 py-20">
      <div className="bg-white border border-slate-200 rounded-xl p-10">
        <KeyRound className="w-8 h-8 text-emerald-600 mb-4" />
        <h1 className="font-heading text-3xl tracking-tight mb-2">Reset password</h1>
        {sent ? (
          <p data-testid="forgot-sent-message" className="text-sm text-slate-600 leading-relaxed mt-4">If that email exists, a reset link has been sent. Check your inbox (link expires in 1 hour).</p>
        ) : (
          <>
            <p className="text-sm text-slate-500 mb-8">Enter your email and we'll send you a reset link.</p>
            <form onSubmit={onSubmit} className="space-y-5">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="forgot-email-input"
                className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              <button type="submit" disabled={loading} data-testid="forgot-submit-button"
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-emerald-600 text-white font-medium hover:bg-emerald-500 transition-colors duration-200 disabled:opacity-60">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Send reset link
              </button>
            </form>
          </>
        )}
        <p className="text-sm text-slate-500 mt-6 text-center"><Link to="/login" className="font-medium text-emerald-700">Back to login</Link></p>
      </div>
    </main>
  );
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await authAxios.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password reset! Log in with your new password.");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="max-w-md mx-auto px-4 py-20">
      <div className="bg-white border border-slate-200 rounded-xl p-10">
        <KeyRound className="w-8 h-8 text-emerald-600 mb-4" />
        <h1 className="font-heading text-3xl tracking-tight mb-2">Choose a new password</h1>
        {error && <p data-testid="reset-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-4 py-3 my-5">{error}</p>}
        <form onSubmit={onSubmit} className="space-y-5 mt-6">
          <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password (8+ characters)" data-testid="reset-password-input"
            className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          <button type="submit" disabled={loading} data-testid="reset-submit-button"
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-emerald-600 text-white font-medium hover:bg-emerald-500 transition-colors duration-200 disabled:opacity-60">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Reset password
          </button>
        </form>
      </div>
    </main>
  );
}
